import { z } from "zod";
import { reglageQrSchema } from "./direct-reglages";
import { rangIdSchema } from "./viewers";

/**
 * Le contrat du direct (lot Q1) : ce que la regie lit et ecrit sous `/api/direct/*` et ce que les overlays
 * d'OBS envoient sous `/api/overlay/*`. Les deux cotes le lisent par leur pont `src/app/contract.ts`.
 *
 * Il vit ici, et non dans `contract.ts` de ce paquet, parce que `src/contract.ts` appartient au template :
 * l'app possede `src/app/**`.
 */

/** `POST /api/direct/cle` : la cle d'overlay en clair, rendue cette fois et jamais plus. */
export const reponseCleSchema = z.object({ cle: z.string() });
export type ReponseCle = z.infer<typeof reponseCleSchema>;

/** `GET /api/direct/cle` : une cle est-elle posee, et depuis quand. Jamais la cle, ni son empreinte. */
export const etatCleSchema = z.object({
  posee: z.boolean(),
  creeeLe: z.string().nullable(),
});
export type EtatCle = z.infer<typeof etatCleSchema>;

/** La scene que OBS affiche, telle que la regie la lit : son nom, depuis quand, et quel overlay l'a signalee. */
export const sceneActiveSchema = z.object({
  nom: z.string(),
  depuis: z.string(),
  source: z.string(),
});
export type SceneActive = z.infer<typeof sceneActiveSchema>;

/** `GET /api/direct/scene` : la derniere scene signalee, ou `null` tant qu'aucun overlay n'en a signale. */
export const reponseSceneSchema = z.object({ scene: sceneActiveSchema.nullable() });
export type ReponseScene = z.infer<typeof reponseSceneSchema>;

/**
 * `POST /api/overlay/scene` : ce que l'overlay relaie d'`obsSceneChanged`. Le serveur nettoie et borne les deux
 * textes (100 et 60 caracteres) ; le corps entier ne depasse pas 2 Ko.
 */
export const signalSceneSchema = z.object({
  nom: z.string().min(1),
  source: z.string().min(1),
});
export type SignalScene = z.infer<typeof signalSceneSchema>;

/**
 * Un message du bus du direct (lot Q1), tel que les overlays et la regie le lisent sur le flux SSE : un
 * evenement de la chaine, un message de chat (son auteur seulement : **jamais son texte**, qui ne va pas
 * au bus dans Q1), la scene d'OBS, l'etat du stream, le nombre de presents, les overlays connectes, le relais du PC
 * qui pilote OBS (lot Q0), et le reglage du QR code du stream (`qr`, reserve aux overlays : voir `GENRES_DES_OVERLAYS`).
 */
export const messageDirectSchema = z.discriminatedUnion("genre", [
  z.object({
    genre: z.literal("evenement"),
    id: z.string(),
    type: z.string(),
    survenuLe: z.string(),
    viewer: z.object({ id: z.string(), nom: z.string() }).nullable(),
    donnees: z.record(z.string(), z.unknown()),
    rejoue: z.boolean(),
  }),
  z.object({ genre: z.literal("chat"), viewerId: z.string().nullable() }),
  z.object({ genre: z.literal("scene"), nom: z.string(), depuis: z.string() }),
  z.object({ genre: z.literal("stream"), etat: z.enum(["ouvert", "ferme"]) }),
  z.object({ genre: z.literal("presents"), nombre: z.number().int() }),
  // Un viewer monte de rang (renommee, ticket 2 du socle) : son nom affiche et son identifiant en base, jamais
  // son login ni son identifiant Twitch.
  z.object({
    genre: z.literal("montee"),
    viewer: z.object({ id: z.string(), nom: z.string() }).nullable(),
    rangAvant: rangIdSchema,
    rangApres: rangIdSchema,
  }),
  z.object({
    genre: z.literal("overlays"),
    connectes: z.array(z.object({ nom: z.string(), depuis: z.string() })),
  }),
  // Le relais du PC qui pilote OBS s'est branche, a rendu son etat ou est parti (lot Q0) : la regie relit
  // `GET /api/obs/etat`. Un drapeau seulement. **Jamais un ordre, ni rien de ce qu'il porte** : les overlays lisent ce
  // bus, et un ordre peut porter la cle TikTok.
  z.object({ genre: z.literal("obs"), relais: z.boolean() }),
  // Le QR code du stream change de coin, ou se montre ou se cache (ticket 3 du TCG, PR 7). Il part aux overlays.
  z.object({ genre: z.literal("qr"), ...reglageQrSchema.shape }),
]);
export type MessageDirect = z.infer<typeof messageDirectSchema>;

/**
 * Les genres du bus que seuls les overlays d'OBS recoivent. Le bus sert tout le monde, mais ni la regie ni le flux
 * d'un viewer n'ont a voir ce qui se passe a l'ecran du stream : `fluxSse` (`flux.ts` du backend) les ecarte, en
 * direct comme au rejeu, sauf pour un flux d'overlay.
 */
export const GENRES_DES_OVERLAYS: readonly MessageDirect["genre"][] = ["qr"];

/** Le nom d'un overlay sur son flux : de 1 a 32 caracteres, minuscules, chiffres et tiret. */
export const nomOverlaySchema = z.string().regex(/^[a-z0-9-]{1,32}$/);

/** `GET /api/overlay/flux?nom=` : le nom que l'overlay se donne, que la regie liste parmi les connectes. */
export const fluxOverlayQuerySchema = z.object({ nom: nomOverlaySchema });
export type FluxOverlayQuery = z.infer<typeof fluxOverlayQuerySchema>;

/** Les lectures de la regie (etat, fiche, messages) et l'import de l'ancien Castellan : un fichier chacun. */
export * from "./direct-import";
export * from "./direct-lecture";
/** Les gestes de la regie : le titre et le jeu, les secours. */
export * from "./direct-regie";
/** Les reglages des overlays d'OBS : le coin et la visibilite du QR code. */
export * from "./direct-reglages";
